// --- GPA -------------------------------------------------------------------

/** US 4.0-scale grade → grade points. */
export const GRADE_POINTS: Record<string, number> = {
  "A+": 4.0,
  A: 4.0,
  "A-": 3.7,
  "B+": 3.3,
  B: 3.0,
  "B-": 2.7,
  "C+": 2.3,
  C: 2.0,
  "C-": 1.7,
  "D+": 1.3,
  D: 1.0,
  "D-": 0.7,
  F: 0.0,
};

export const GRADES = Object.keys(GRADE_POINTS);

export interface Course {
  grade: string;
  credits: number;
}

/** Credit-weighted GPA. Returns null when there are no valid credits. */
export function gpa(courses: Course[]): number | null {
  let points = 0;
  let credits = 0;
  for (const c of courses) {
    const gp = GRADE_POINTS[c.grade];
    if (gp === undefined || !Number.isFinite(c.credits) || c.credits <= 0)
      continue;
    points += gp * c.credits;
    credits += c.credits;
  }
  return credits > 0 ? points / credits : null;
}

// --- Fractions -------------------------------------------------------------

export interface Fraction {
  n: number;
  d: number;
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

/** Reduce a fraction to lowest terms, keeping the sign on the numerator. */
export function simplify({ n, d }: Fraction): Fraction {
  if (d === 0) return { n: Number.NaN, d: 0 };
  const g = gcd(n, d);
  const sign = d < 0 ? -1 : 1;
  return { n: (n / g) * sign, d: Math.abs(d / g) };
}

export type FractionOp = "add" | "subtract" | "multiply" | "divide";

export function applyFraction(
  a: Fraction,
  b: Fraction,
  op: FractionOp,
): Fraction {
  switch (op) {
    case "add":
      return simplify({ n: a.n * b.d + b.n * a.d, d: a.d * b.d });
    case "subtract":
      return simplify({ n: a.n * b.d - b.n * a.d, d: a.d * b.d });
    case "multiply":
      return simplify({ n: a.n * b.n, d: a.d * b.d });
    case "divide":
      return simplify({ n: a.n * b.d, d: a.d * b.n });
  }
}

// --- Statistics ------------------------------------------------------------

export interface Stats {
  count: number;
  sum: number;
  mean: number;
  median: number;
  mode: number[];
  min: number;
  max: number;
  range: number;
  variance: number;
  stdDev: number;
}

/** Parse a free-form string of numbers (comma / space / newline separated). */
export function parseNumbers(input: string): number[] {
  return input
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map(Number)
    .filter((n) => Number.isFinite(n));
}

/** Descriptive statistics for a sample. Returns null when empty. */
export function stats(nums: number[]): Stats | null {
  if (nums.length === 0) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const count = sorted.length;
  const sum = sorted.reduce((a, b) => a + b, 0);
  const mean = sum / count;
  const median =
    count % 2 === 1
      ? sorted[(count - 1) / 2]
      : (sorted[count / 2 - 1] + sorted[count / 2]) / 2;

  const freq = new Map<number, number>();
  let maxFreq = 0;
  for (const n of sorted) {
    const f = (freq.get(n) ?? 0) + 1;
    freq.set(n, f);
    if (f > maxFreq) maxFreq = f;
  }
  const mode =
    maxFreq <= 1
      ? []
      : [...freq.entries()].filter(([, f]) => f === maxFreq).map(([n]) => n);

  const variance = sorted.reduce((a, n) => a + (n - mean) ** 2, 0) / count;

  return {
    count,
    sum,
    mean,
    median,
    mode,
    min: sorted[0],
    max: sorted[count - 1],
    range: sorted[count - 1] - sorted[0],
    variance,
    stdDev: Math.sqrt(variance),
  };
}
